import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@one-thd/sui-atomic-components';
import { DateCalendar } from '@one-thd/sui-date-pickers';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { FeatureSwitches } from '../../../constants/feature-switches';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import { TDateSelectValues } from '../../../models/common';
import { IPHRequest } from '../../../models/purchase-history';
import { DateRanges } from '../../../utils/DateRanges';
import { getWindow } from '../../../utils/general';
import {
  getEffectiveFilterDateRange,
  getFilterDetailsFromSession,
} from '../../../utils/purchase-history/common';
import {
  isPOJobPaymentSearchEnabledForUser,
  modifyDateFormat,
  parseDateToString,
} from '../../../utils/purchase-history/order-filter-utils';
import { OrderFilterDateSection } from './OrderFilterDateSection';
import { OrderFilterGroup } from './OrderFilterGroup';
import { OrderFilterFooter } from './OrderFilterFooter';
import { OrderFilterUserSection } from './OrderFilterUserSection';

interface IOrderFilterDrawer {
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isOpen: boolean) => void;
  dateFilterDot: { date: boolean; user: boolean };
  setDateFilterDot: React.Dispatch<
    React.SetStateAction<{
      date: boolean;
      user: boolean;
    }>
  >;
  permissionRulesCheck: boolean;
}

export const OrderFilterDrawer = ({
  isDrawerOpen,
  setIsDrawerOpen,
  dateFilterDot,
  setDateFilterDot,
  permissionRulesCheck,
}: IOrderFilterDrawer) => {
  const { username, svocID } = useThdCustomer();

  const {
    dateSelectorValue,
    pHRequest,
    setDateSelectorValue,
    setPHRequest,
    setAlertState,
    poJobFilters,
    paymentFilters,
  } = usePurchaseHistoryContext();

  const poJobNameTileOptions = poJobFilters.map((filter) => ({
    label: filter,
    value: filter,
  }));

  const paymentTileOptions = paymentFilters.map((filter) => ({
    label: filter.last4,
    value: filter.last4,
  }));

  const sessionPHFilter = getFilterDetailsFromSession();

  const [poJobNameFilter, setPOJobNameFilter] = useState<string>(
    pHRequest?.filters?.find((filter) => filter.name === 'poJobName')?.value ||
      '',
  );
  const [paymentFilter, setPaymentFilter] = useState<string>(
    pHRequest?.filters?.find((filter) => filter.name === 'payment')?.value ||
      '',
  );

  const isPOJobAndPaymentFilterEnabled =
    FeatureSwitches.enablePOJOBAndPaymentSearch === true &&
    isPOJobPaymentSearchEnabledForUser(svocID ?? '');

  const [filterSettings, setFilterSettings] = useState({
    userView: pHRequest?.resultsFilter || 'allOrders',
    customDateUsed: false,
    showStartCalendar: false,
    showEndCalendar: false,
    invalidStartDate: false,
    invalidEndDate: false,
  });
  const [dateSettings, setDateSettings] = useState<{
    selectedDate: TDateSelectValues | null;
    startDate: DateTime | string | null;
    endDate: DateTime | string | null;
  }>(() => {
    const { startDate, endDate } = getEffectiveFilterDateRange(
      pHRequest,
      sessionPHFilter,
    );
    return {
      selectedDate: sessionPHFilter?.selectedDate ?? dateSelectorValue,
      startDate: startDate ?? null,
      endDate: endDate ?? null,
    };
  });

  const maxDate = DateTime.now().startOf('day').toString();
  const minDate = DateTime.now()
    .minus({ months: 84 })
    .startOf('day')
    .toString();
  const filterDotBaseClassName =
    'sui-absolute sui-left-[40px] sui-size-[10px] sui-rounded-full sui-bg-[#f96302] sui-ml-[8px] sui-z-[100]';

  const closeFilterDrawer = () => {
    setIsDrawerOpen(false);
  };

  const onDateSelect = (
    _event: React.SyntheticEvent<Element, Event>,
    date: string,
  ): void => {
    if (date === dateSettings.selectedDate) {
      return;
    }
    if (date) {
      setFilterSettings((prevState) => ({
        ...prevState,
        customDateUsed: false,
      }));
      setDateSettings((prevState) => ({
        ...prevState,
        selectedDate: date as TDateSelectValues,
      }));
      const startDateStr =
        DateRanges?.[(date as keyof typeof DateRanges) ?? '']?.startDate || '';
      const endDateStr =
        DateRanges?.[(date as keyof typeof DateRanges) ?? '']?.endDate || '';
      const newStartDate = parseDateToString(startDateStr);
      const newEndDate = parseDateToString(endDateStr);
      setDateSettings((prevState) => ({
        ...prevState,
        startDate: startDateStr ? newStartDate : null,
        endDate: endDateStr ? newEndDate : null,
      }));
    }
  };

  const hasFilterChanges = (
    previousFilters: { name: string; value: string }[] = [],
    newFilters: { name: string; value: string }[] = [],
  ) => {
    if (previousFilters.length !== newFilters.length) {
      return true;
    }

    return previousFilters.some(
      (filter, index) =>
        filter.name !== newFilters[index]?.name ||
        filter.value !== newFilters[index]?.value,
    );
  };

  const getFilters = () => {
    const filters: { name: string; value: string }[] = [];
    if (poJobNameFilter) {
      filters.push({ name: 'poJobName', value: poJobNameFilter });
    }
    if (paymentFilter) {
      filters.push({ name: 'payment', value: paymentFilter });
    }
    return filters;
  };

  const handleUserViewChange = () => {
    setPHRequest((prevState: IPHRequest) => {
      const newFilters = isPOJobAndPaymentFilterEnabled
        ? getFilters()
        : prevState.filters;
      const hasChangedFilters =
        isPOJobAndPaymentFilterEnabled &&
        hasFilterChanges(prevState.filters ?? [], newFilters ?? []);
      const nextResultsFilter = filterSettings.userView ?? undefined;

      if (
        prevState.resultsFilter === nextResultsFilter &&
        prevState.pageNumber === 1 &&
        !hasChangedFilters
      ) {
        return prevState;
      }

      return {
        ...prevState,
        resultsFilter: nextResultsFilter,
        pageNumber: 1,
        filters: hasChangedFilters ? newFilters : prevState.filters,
      };
    });
    getWindow()?.sessionStorage.setItem(
      'PHFilter',
      JSON.stringify({ ...dateSettings, ...filterSettings }),
    );
    setDateFilterDot((prevState) => ({ ...prevState, user: true }));
    closeFilterDrawer();
  };

  const handleDateViewChange = () => {
    const [newStartDate, newEndDate] = modifyDateFormat(
      dateSettings.startDate,
      dateSettings.endDate,
    );

    setDateSelectorValue(
      (dateSettings.selectedDate as TDateSelectValues) ||
        (FeatureSwitches.enableLSSCPS25MonthsDefault
          ? ('last25Months' as TDateSelectValues)
          : ('last6Months' as TDateSelectValues)),
    );
    setPHRequest((prevState) => {
      const newFilters = isPOJobAndPaymentFilterEnabled
        ? getFilters()
        : prevState.filters;
      const hasChangedFilters =
        isPOJobAndPaymentFilterEnabled &&
        hasFilterChanges(prevState.filters ?? [], newFilters ?? []);
      const nextStartDate = dateSettings.startDate ? newStartDate ?? '' : '';
      const nextEndDate = dateSettings.endDate ? newEndDate ?? '' : '';
      if (
        prevState.startDate === nextStartDate &&
        prevState.endDate === nextEndDate &&
        prevState.pageNumber === 1 &&
        !hasChangedFilters
      ) {
        return prevState;
      }

      return {
        ...prevState,
        startDate: nextStartDate,
        endDate: nextEndDate,
        pageNumber: 1,
        filters: hasChangedFilters ? newFilters : prevState.filters,
      };
    });
    getWindow()?.sessionStorage.setItem(
      'PHFilter',
      JSON.stringify({ ...dateSettings, ...filterSettings }),
    );
    setDateFilterDot((prevState) => ({ ...prevState, date: true }));
    closeFilterDrawer();
  };

  const applyDateDisabled = () => {
    if (filterSettings.showStartCalendar) {
      return !dateSettings.startDate;
    }
    if (filterSettings.showEndCalendar) {
      return !dateSettings.endDate;
    }
    return false;
  };

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={closeFilterDrawer}
      style={{ zIndex: 20000000010 }}
    >
      {filterSettings.showStartCalendar || filterSettings.showEndCalendar ? (
        <DrawerHeader
          title="Custom Range"
          onBack={() =>
            setFilterSettings((prevState) => ({
              ...prevState,
              showStartCalendar: false,
              showEndCalendar: false,
            }))
          }
          onClose={closeFilterDrawer}
        />
      ) : (
        <DrawerHeader title="Filter" onClose={closeFilterDrawer} />
      )}
      <DrawerBody>
        {filterSettings.showStartCalendar || filterSettings.showEndCalendar ? (
          <div>
            <DateCalendar
              minDate={minDate}
              maxDate={maxDate}
              value={
                filterSettings.showStartCalendar
                  ? (dateSettings.startDate as string) || ''
                  : (dateSettings.endDate as string) || ''
              }
              onChange={(_event, value) => {
                setFilterSettings((prevState) => ({
                  ...prevState,
                  customDateUsed: true,
                }));

                const modifyDate = value.toString().split('T')[0];
                const newDate = parseDateToString(modifyDate);

                if (filterSettings.showStartCalendar) {
                  setDateSettings((prevState) => ({
                    ...prevState,
                    startDate: newDate,
                    selectedDate: 'custom',
                  }));
                } else if (filterSettings.showEndCalendar) {
                  setDateSettings((prevState) => ({
                    ...prevState,
                    endDate: newDate,
                    selectedDate: 'custom',
                  }));
                }
              }}
            />
          </div>
        ) : (
          <>
            {permissionRulesCheck && (
              <OrderFilterUserSection
                showDot={dateFilterDot.user}
                filterDotBaseClassName={filterDotBaseClassName}
                userView={filterSettings.userView}
                username={username}
                setFilterSettings={setFilterSettings}
              />
            )}
            <OrderFilterDateSection
              showDot={dateFilterDot.date}
              filterDotBaseClassName={filterDotBaseClassName}
              dateSettings={dateSettings}
              setDateSettings={setDateSettings}
              setFilterSettings={setFilterSettings}
              onDateSelect={onDateSelect}
            />
            {isPOJobAndPaymentFilterEnabled && (
              <>
                <div className="sui-border-b-1 sui-border-solid sui-border-strong">
                  <OrderFilterGroup
                    title="PO/Job Name"
                    tileOptions={poJobNameTileOptions}
                    selectedOption={poJobNameFilter}
                    setSelectedOption={setPOJobNameFilter}
                  />
                </div>
                <div className="sui-border-b-1 sui-border-solid sui-border-strong">
                  <OrderFilterGroup
                    title="Payment"
                    tileOptions={paymentTileOptions}
                    selectedOption={paymentFilter}
                    setSelectedOption={setPaymentFilter}
                  />
                </div>
              </>
            )}
          </>
        )}
      </DrawerBody>
      {filterSettings.showStartCalendar || filterSettings.showEndCalendar ? (
        <DrawerFooter>
          <Button
            variant="primary"
            fullWidth
            disabled={applyDateDisabled()}
            onClick={() =>
              setFilterSettings((prevState) => ({
                ...prevState,
                showStartCalendar: false,
                showEndCalendar: false,
              }))
            }
          >
            Apply
          </Button>
        </DrawerFooter>
      ) : (
        <OrderFilterFooter
          filterSettings={filterSettings}
          pHRequestResultsFilter={pHRequest.resultsFilter}
          dateSettings={dateSettings}
          username={username}
          poJobNameFilter={poJobNameFilter}
          paymentFilter={paymentFilter}
          setFilterSettings={setFilterSettings}
          onDateSelect={onDateSelect}
          setPOJobNameFilter={setPOJobNameFilter}
          setPaymentFilter={setPaymentFilter}
          onViewResults={() => {
            if (filterSettings.userView !== pHRequest.resultsFilter) {
              handleUserViewChange();
              return;
            }

            setAlertState(null);
            handleDateViewChange();
          }}
        />
      )}
    </Drawer>
  );
};
